/**
 * Notes API service using BaseApi pattern for core CRUD
 */

import {
  AddNoteMember,
  CreateNote,
  CreateNoteCategory,
  CreateNotePage,
  Note,
  NoteCategory,
  NoteCategoryWithCount,
  NoteMember,
  NotePageSummaryWithCreator,
  NotePageWithCreator,
  NoteSnapshotWithCreator,
  NoteVisibilityEnum,
  NoteWithRelations,
  RecordAccessSyncResult,
  RecordAccessUser,
  TaskPriorityEnum,
  UpdateNoteCategory,
  UpdateNoteInput,
  UpdateNoteMember,
  UpdateNotePage,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { getCurrentClientPortalContext } from './client-portal-context'
import { clientPortalApi } from './client-portal.api'
import { createSingletonApi } from './singleton-factory'

export interface NoteActionItem {
  id: string
  noteId: string
  notePageId: string | null
  organizationId: string
  text: string
  description: string | null
  order: number
  assigneeId: string | null
  assigneeIds: string[]
  dueDate: string | null
  groupId: string | null
  taskTypeDefId: string | null
  targetProjectId: string | null
  isConverted: boolean
  convertedTaskId: string | null
  createdAt: string
  updatedAt: string
  assignee?: {
    id: string
    firstName: string | null
    lastName: string | null
    displayName: string | null
    image: string | null
  } | null
}

export interface ConvertActionItemsOptions {
  groupId?: string
  status?: string
  sprintId?: string
  cycleInstanceId?: string
  bucketId?: string
  priority?: TaskPriorityEnum
  dueDate?: string
  assigneeIds?: string[]
  tagIds?: string[]
  customFields?: Record<string, unknown>
}

export type NotePdfExportScope = 'all' | 'latest' | 'selected'

export interface ExportNotePdfOptions {
  scope?: NotePdfExportScope
  pageIds?: string[]
}

interface NoteQueryParams {
  search?: string
  visibility?: NoteVisibilityEnum
  creatorId?: string
  isPinned?: boolean
  isCollection?: boolean
  /** 'personal' = notes created by or shared with the current user */
  scope?: 'personal'
  /** true = only archived notes; omitted/false = only non-archived */
  archived?: boolean
  moduleType?: string
  moduleId?: string
  page?: number
  limit?: number
}

interface NotesResponse {
  notes: NoteWithRelations[]
  total: number
}

class NoteApiService extends BaseApi<Note, CreateNote, UpdateNoteInput> {
  constructor() {
    super({
      baseEndpoint: '/notes',
      responseNormalization: true,
    })
  }

  private getClientPortalEntityId(): string | null {
    return getCurrentClientPortalContext()?.entityId ?? null
  }

  private encodeContentToBase64(content: Uint8Array): string {
    let binary = ''
    for (let i = 0; i < content.length; i++) {
      binary += String.fromCharCode(content[i])
    }
    return btoa(binary)
  }

  // Module counts
  async getModuleCounts(): Promise<{ allNotes: number }> {
    return this.makeRequest<{ allNotes: number }>(
      `${this.config.baseEndpoint}/module-counts`,
    )
  }

  // Note CRUD operations
  async getNotes(params?: NoteQueryParams): Promise<NotesResponse> {
    const queryParams = new URLSearchParams()

    if (params?.search) queryParams.append('search', params.search)
    if (params?.visibility) queryParams.append('visibility', params.visibility)
    if (params?.creatorId) queryParams.append('creatorId', params.creatorId)
    if (params?.isPinned !== undefined)
      queryParams.append('isPinned', String(params.isPinned))
    if (params?.isCollection !== undefined)
      queryParams.append('isCollection', String(params.isCollection))
    if (params?.scope) queryParams.append('scope', params.scope)
    if (params?.archived) queryParams.append('archived', 'true')
    if (params?.moduleType) queryParams.append('moduleType', params.moduleType)
    if (params?.moduleId) queryParams.append('moduleId', params.moduleId)
    if (params?.page) queryParams.append('page', String(params.page))
    if (params?.limit) queryParams.append('limit', String(params.limit))

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest<NotesResponse>(
      `${this.config.baseEndpoint}${queryString}`,
    )
  }

  async getNote(id: string): Promise<NoteWithRelations> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getNote(
        portalEntityId,
        id,
      ) as unknown as Promise<NoteWithRelations>
    }
    return this.findById(id) as Promise<NoteWithRelations>
  }

  async createNote(data: CreateNote): Promise<Note> {
    return super.create(data)
  }

  async updateNote(id: string, data: UpdateNoteInput): Promise<Note> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.updateNote(
        portalEntityId,
        id,
        data,
      ) as unknown as Promise<Note>
    }
    return this.update(id, data)
  }

  async deleteNote(id: string): Promise<void> {
    return this.delete(id)
  }

  // Yjs content operations
  async getNoteContent(
    id: string,
  ): Promise<{ noteId: string; content: string | null }> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getNoteContent(portalEntityId, id)
    }
    return this.makeRequest<{ noteId: string; content: string | null }>(
      `${this.config.baseEndpoint}/${id}/content`,
    )
  }

  async updateNoteContent(id: string, content: Uint8Array): Promise<Note> {
    const portalEntityId = this.getClientPortalEntityId()
    const base64Content = this.encodeContentToBase64(content)

    if (portalEntityId) {
      return clientPortalApi.updateNoteContent(
        portalEntityId,
        id,
        base64Content,
      ) as unknown as Promise<Note>
    }

    return this.makeRequest<Note>(`${this.config.baseEndpoint}/${id}/content`, {
      method: 'PUT',
      body: { content: base64Content },
    })
  }

  // Member management
  async getNoteMembers(noteId: string): Promise<NoteMember[]> {
    return this.makeRequest<NoteMember[]>(
      `${this.config.baseEndpoint}/${noteId}/members`,
    )
  }

  async addNoteMember(
    noteId: string,
    data: AddNoteMember,
  ): Promise<NoteMember> {
    return this.makeRequest<NoteMember>(
      `${this.config.baseEndpoint}/${noteId}/members`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async updateNoteMember(
    noteId: string,
    memberId: string,
    data: UpdateNoteMember,
  ): Promise<NoteMember> {
    return this.makeRequest<NoteMember>(
      `${this.config.baseEndpoint}/${noteId}/members/${memberId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
  }

  async removeNoteMember(noteId: string, memberId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${noteId}/members/${memberId}`,
      {
        method: 'DELETE',
        skipJsonParse: true,
      },
    )
  }

  // Category operations
  async getCategories(params?: {
    moduleType?: string
    moduleId?: string
  }): Promise<NoteCategoryWithCount[]> {
    const queryParams = new URLSearchParams()
    if (params?.moduleType) queryParams.append('moduleType', params.moduleType)
    if (params?.moduleId) queryParams.append('moduleId', params.moduleId)
    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest<NoteCategoryWithCount[]>(
      `${this.config.baseEndpoint}/categories${queryString}`,
    )
  }

  async getCategory(categoryId: string): Promise<NoteCategory> {
    return this.makeRequest<NoteCategory>(
      `${this.config.baseEndpoint}/categories/${categoryId}`,
    )
  }

  async createCategory(data: CreateNoteCategory): Promise<NoteCategory> {
    return this.makeRequest<NoteCategory>(
      `${this.config.baseEndpoint}/categories`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async updateCategory(
    categoryId: string,
    data: UpdateNoteCategory,
  ): Promise<NoteCategory> {
    return this.makeRequest<NoteCategory>(
      `${this.config.baseEndpoint}/categories/${categoryId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
  }

  async deleteCategory(categoryId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/categories/${categoryId}`,
      {
        method: 'DELETE',
        skipJsonParse: true,
      },
    )
  }

  async assignNoteToCategory(
    noteId: string,
    categoryId: string | null,
  ): Promise<Note> {
    return this.makeRequest<Note>(
      `${this.config.baseEndpoint}/${noteId}/category`,
      {
        method: 'PUT',
        body: { categoryId },
      },
    )
  }

  // =====================
  // Note Page Operations
  // =====================

  /**
   * List all pages for a note (metadata only, no content)
   */
  async listPages(noteId: string): Promise<NotePageSummaryWithCreator[]> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.listNotePages(portalEntityId, noteId)
    }

    const response = await this.makeRequest<{
      pages: NotePageSummaryWithCreator[]
    }>(`${this.config.baseEndpoint}/${noteId}/pages`)
    return response.pages
  }

  /**
   * Get a single page with full content
   */
  async getPage(noteId: string, pageId: string): Promise<NotePageWithCreator> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getNotePage(portalEntityId, noteId, pageId)
    }

    const response = await this.makeRequest<{ page: NotePageWithCreator }>(
      `${this.config.baseEndpoint}/${noteId}/pages/${pageId}`,
    )
    return response.page
  }

  /**
   * Create a new page in a note
   */
  async createPage(
    noteId: string,
    data: CreateNotePage,
  ): Promise<NotePageWithCreator> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.createNotePage(portalEntityId, noteId, data)
    }

    const response = await this.makeRequest<{ page: NotePageWithCreator }>(
      `${this.config.baseEndpoint}/${noteId}/pages`,
      {
        method: 'POST',
        body: data,
      },
    )
    return response.page
  }

  /**
   * Update a page
   */
  async updatePage(
    noteId: string,
    pageId: string,
    data: UpdateNotePage,
  ): Promise<NotePageWithCreator> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.updateNotePage(
        portalEntityId,
        noteId,
        pageId,
        data,
      )
    }

    const response = await this.makeRequest<{ page: NotePageWithCreator }>(
      `${this.config.baseEndpoint}/${noteId}/pages/${pageId}`,
      {
        method: 'PUT',
        body: data,
      },
    )
    return response.page
  }

  /**
   * Delete a page
   */
  async deletePage(noteId: string, pageId: string): Promise<void> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      await clientPortalApi.deleteNotePage(portalEntityId, noteId, pageId)
      return
    }

    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${noteId}/pages/${pageId}`,
      {
        method: 'DELETE',
        skipJsonParse: true,
      },
    )
  }

  /**
   * Reorder pages
   */
  async reorderPages(noteId: string, pageIds: string[]): Promise<void> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      await clientPortalApi.reorderNotePages(portalEntityId, noteId, pageIds)
      return
    }

    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${noteId}/pages/reorder`,
      {
        method: 'PUT',
        body: { pageIds },
        skipJsonParse: true,
      },
    )
  }

  /**
   * Duplicate a page (creates a copy with content)
   */
  async duplicatePage(
    noteId: string,
    pageId: string,
  ): Promise<NotePageWithCreator> {
    // Get the source page with its content
    const sourcePage = await this.getPage(noteId, pageId)
    const sourceContent = await this.getPageContent(noteId, pageId)

    // Create a new page with copied title
    const newPage = await this.createPage(noteId, {
      title: `${sourcePage.title || 'Untitled'} (Copy)`,
      source: sourcePage.source,
      sourceMetadata: sourcePage.sourceMetadata,
    })

    // Copy the content if it exists
    if (sourceContent.content) {
      // Convert base64 back to Uint8Array
      const binary = atob(sourceContent.content)
      const bytes = new Uint8Array(binary.length)
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
      }
      await this.updatePageContent(noteId, newPage.id, bytes)
    }

    return newPage
  }

  /**
   * Convert a single note to a collection
   */
  async convertToCollection(noteId: string): Promise<NotePageWithCreator> {
    const response = await this.makeRequest<{ page: NotePageWithCreator }>(
      `${this.config.baseEndpoint}/${noteId}/convert-to-collection`,
      {
        method: 'POST',
      },
    )
    return response.page
  }

  /**
   * Get page content (Yjs state) for collaborative editing
   */
  async getPageContent(
    noteId: string,
    pageId: string,
  ): Promise<{ pageId: string; content: string | null }> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getNotePageContent(portalEntityId, noteId, pageId)
    }

    return this.makeRequest<{ pageId: string; content: string | null }>(
      `${this.config.baseEndpoint}/${noteId}/pages/${pageId}/content`,
    )
  }

  /**
   * Update page content (Yjs state)
   */
  async updatePageContent(
    noteId: string,
    pageId: string,
    content: Uint8Array,
  ): Promise<void> {
    const portalEntityId = this.getClientPortalEntityId()
    const base64Content = this.encodeContentToBase64(content)

    if (portalEntityId) {
      await clientPortalApi.updateNotePageContent(
        portalEntityId,
        noteId,
        pageId,
        base64Content,
      )
      return
    }

    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${noteId}/pages/${pageId}/content`,
      {
        method: 'PUT',
        body: { content: base64Content },
        skipJsonParse: true,
      },
    )
  }

  // =====================
  // Record Access Operations
  // =====================

  /**
   * Get users who have access to a record (for RECORD visibility preview)
   */
  async getRecordAccessUsers(
    moduleType: string,
    moduleId: string,
  ): Promise<RecordAccessUser[]> {
    return this.makeRequest<RecordAccessUser[]>(
      `${this.config.baseEndpoint}/record-access/${moduleType}/${moduleId}`,
    )
  }

  /**
   * Manually sync note access (for RECORD visibility notes)
   */
  async syncNoteAccess(noteId: string): Promise<RecordAccessSyncResult> {
    return this.makeRequest<RecordAccessSyncResult>(
      `${this.config.baseEndpoint}/${noteId}/sync-access`,
      {
        method: 'POST',
      },
    )
  }

  // =====================
  // Snapshot Operations
  // =====================

  /**
   * Get snapshots for a note
   */
  async getSnapshots(
    noteId: string,
    options?: { pageId?: string; skip?: number; take?: number },
  ): Promise<NoteSnapshotWithCreator[]> {
    const queryParams = new URLSearchParams()
    if (options?.pageId) queryParams.append('pageId', options.pageId)
    if (options?.skip) queryParams.append('skip', String(options.skip))
    if (options?.take) queryParams.append('take', String(options.take))
    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''

    return this.makeRequest<NoteSnapshotWithCreator[]>(
      `${this.config.baseEndpoint}/${noteId}/snapshots${queryString}`,
    )
  }

  /**
   * Get a single snapshot with content
   */
  async getSnapshot(
    noteId: string,
    snapshotId: string,
  ): Promise<NoteSnapshotWithCreator & { content: string | null }> {
    return this.makeRequest<
      NoteSnapshotWithCreator & { content: string | null }
    >(`${this.config.baseEndpoint}/${noteId}/snapshots/${snapshotId}`)
  }

  /**
   * Create a snapshot manually
   */
  async createSnapshot(
    noteId: string,
    pageId?: string,
  ): Promise<{ snapshotId: string | null; created: boolean }> {
    return this.makeRequest<{ snapshotId: string | null; created: boolean }>(
      `${this.config.baseEndpoint}/${noteId}/snapshots`,
      {
        method: 'POST',
        body: { pageId },
      },
    )
  }

  /**
   * Restore from a snapshot
   */
  async restoreSnapshot(
    noteId: string,
    snapshotId: string,
  ): Promise<{ success: boolean }> {
    return this.makeRequest<{ success: boolean }>(
      `${this.config.baseEndpoint}/${noteId}/snapshots/${snapshotId}/restore`,
      {
        method: 'POST',
      },
    )
  }

  /**
   * Export page as PDF - returns a blob for download
   */
  async exportPagePdf(noteId: string, pageId: string): Promise<Blob> {
    return this.makeBlobRequest(
      `${this.config.baseEndpoint}/${noteId}/pages/export/pdf/${pageId}`,
      { acceptType: 'application/pdf' },
    )
  }

  /**
   * Export entire note (collection) as PDF - returns a blob for download
   */
  async exportNotePdf(
    noteId: string,
    options?: ExportNotePdfOptions,
  ): Promise<Blob> {
    const queryParams = new URLSearchParams()
    if (options?.scope) {
      queryParams.set('scope', options.scope)
    }
    if (options?.pageIds?.length) {
      queryParams.set('pageIds', options.pageIds.join(','))
    }
    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''

    return this.makeBlobRequest(
      `${this.config.baseEndpoint}/${noteId}/export/pdf${queryString}`,
      { acceptType: 'application/pdf' },
    )
  }

  /**
   * Bulk delete notes (soft delete)
   */
  async bulkDeleteNotes(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(
      `${this.config.baseEndpoint}/bulk-delete`,
      {
        method: 'POST',
        body: { ids },
      },
    )
  }

  /**
   * Duplicate a note with optional new title and record link
   */
  async duplicateNote(
    noteId: string,
    options: {
      title?: string
      moduleType?: string
      moduleId?: string
    },
  ): Promise<Note> {
    return this.makeRequest<Note>(
      `${this.config.baseEndpoint}/${noteId}/duplicate`,
      {
        method: 'POST',
        body: options,
      },
    )
  }

  // =====================
  // Action Item Operations
  // =====================

  async generateActionItems(
    noteId: string,
    notePageId?: string,
  ): Promise<NoteActionItem[]> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.generateNoteActionItems(
        portalEntityId,
        noteId,
        notePageId,
      ) as unknown as Promise<NoteActionItem[]>
    }

    return this.makeRequest<NoteActionItem[]>(
      `${this.config.baseEndpoint}/${noteId}/action-items/generate`,
      {
        method: 'POST',
        body: { notePageId },
      },
    )
  }

  async suggestActionItemOrganization(
    noteId: string,
    projectId: string,
    notePageId?: string,
  ): Promise<NoteActionItem[]> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.suggestNoteActionItemOrganization(
        portalEntityId,
        noteId,
        projectId,
        notePageId,
      ) as unknown as Promise<NoteActionItem[]>
    }

    return this.makeRequest<NoteActionItem[]>(
      `${this.config.baseEndpoint}/${noteId}/action-items/suggest-organization`,
      {
        method: 'POST',
        body: { projectId, notePageId },
      },
    )
  }

  async getActionItems(
    noteId: string,
    notePageId?: string,
  ): Promise<NoteActionItem[]> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getNoteActionItems(
        portalEntityId,
        noteId,
        notePageId,
      ) as unknown as Promise<NoteActionItem[]>
    }

    const queryParams = new URLSearchParams()
    if (notePageId) queryParams.append('notePageId', notePageId)
    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest<NoteActionItem[]>(
      `${this.config.baseEndpoint}/${noteId}/action-items${queryString}`,
    )
  }

  async updateActionItem(
    noteId: string,
    itemId: string,
    data: {
      text?: string
      description?: string | null
      assigneeId?: string | null
      assigneeIds?: string[]
      dueDate?: string | null
      order?: number
      groupId?: string | null
      taskTypeDefId?: string | null
      targetProjectId?: string | null
    },
  ): Promise<NoteActionItem> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.updateNoteActionItem(
        portalEntityId,
        noteId,
        itemId,
        data,
      ) as unknown as Promise<NoteActionItem>
    }

    return this.makeRequest<NoteActionItem>(
      `${this.config.baseEndpoint}/${noteId}/action-items/${itemId}`,
      {
        method: 'PATCH',
        body: data,
      },
    )
  }

  async deleteActionItem(noteId: string, itemId: string): Promise<void> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      await clientPortalApi.deleteNoteActionItem(portalEntityId, noteId, itemId)
      return
    }

    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${noteId}/action-items/${itemId}`,
      {
        method: 'DELETE',
        skipJsonParse: true,
      },
    )
  }

  async convertActionItemsToTasks(
    noteId: string,
    actionItemIds: string[],
    projectId: string,
    options?: ConvertActionItemsOptions,
  ): Promise<{ taskIds: string[] }> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.convertNoteActionItemsToTasks(
        portalEntityId,
        noteId,
        actionItemIds,
        projectId,
        options,
      )
    }

    return this.makeRequest<{ taskIds: string[] }>(
      `${this.config.baseEndpoint}/${noteId}/action-items/convert-to-tasks`,
      {
        method: 'POST',
        body: { actionItemIds, projectId, ...options },
      },
    )
  }
}

// Export singleton instance
export const noteApi = createSingletonApi(NoteApiService)
